import React, { useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";
import { useRole, usePermissions, usePermission } from "../providers/PermissionsProvider";
import { useSubdomain } from "../providers/SubdomainProvider";
import { DashboardRouter } from "../pages/dashboards/DashboardRouter";
import { PageLoader } from "./page-loader";
import {
  getSubdomainHopTarget,
  hopToSubdomain,
} from "../utils/subdomainRedirect";

/**
 * DashboardRedirect - Per-userType landing after `/dashboard`.
 *
 *   customer        -> /chat            (research workspace)
 *   clinic + phleb  -> /clinic/phleb    (phlebotomist iPad dashboard)
 *   clinic + CSC    -> /clinic          (CSC iPad dashboard)
 *   member / admin  -> <DashboardRouter /> (existing per-role dashboards)
 *
 * Subdomain handling
 * ------------------
 * The Auth0 callback (`callback-page.js`) `navigate()`s users directly to
 * `/dashboard`, which means `LandingRouter` (mounted only at `/`) never
 * runs after login. So a clinic user landing on `platform.localhost/
 * dashboard` would, without intervention, be sent to `/clinic`, denied
 * by `SubdomainRoute allow="clinic"`, bounced to `/chat`, denied by
 * `PermissionRoute permission="chat:access"`, sent back to /dashboard
 * → infinite loop.
 *
 * Fix: when the user's userType doesn't match the current subdomain, do
 * a bare cross-subdomain hop to `<target>.<rest>/dashboard`. On arrival,
 * `AuthenticationGuard` (`withAuthenticationRequired`) sees no local
 * session and triggers its own `loginWithRedirect`, which silently SSOs
 * via Auth0's tenant-domain cookie. See `utils/subdomainRedirect.js` for
 * why we don't use cross-origin `loginWithRedirect` directly (PKCE state
 * cannot cross origins).
 *
 * Covered in `frontend_v2/src/components/__tests__/DashboardRedirect.test.js`.
 */
export const DashboardRedirect = () => {
  const { userType, isCustomer, isClinic, isPhlebotomist, loading } = useRole();
  const { isPlatformAdmin } = usePermissions();
  const { can } = usePermission();
  const { subdomain } = useSubdomain();
  // Guard against firing the hop more than once. window.location.href
  // navigation is asynchronous; React can rerender us in the interim.
  const hopFiredRef = useRef(false);

  const hopTarget = !loading && !isPlatformAdmin
    ? getSubdomainHopTarget(userType, subdomain)
    : null;

  useEffect(() => {
    if (hopTarget && !hopFiredRef.current) {
      hopFiredRef.current = true;
      hopToSubdomain(hopTarget);
    }
  }, [hopTarget]);

  if (loading || hopTarget) {
    return <PageLoader />;
  }

  if (isCustomer) {
    // Custom phleb roles may still be typed customer; never bounce to /chat
    // without chat:access (causes /dashboard <-> /chat redirect loops).
    if (!can('chat:access')) {
      if (can('clinic:phleb_protocol_run')) {
        return <Navigate to="/clinic/phleb" replace />;
      }
      if (can('sample_collection:provider_read')) {
        return <Navigate to="/phleb/collections" replace />;
      }
      return <DashboardRouter />;
    }
    return <Navigate to="/chat" replace />;
  }

  if (isClinic) {
    // Phlebs get the focused phleb queue; everyone else (CSCs, multi-role
    // clinic staff defaulting to CSC) gets the CSC dashboard.
    return <Navigate to={isPhlebotomist ? "/clinic/phleb" : "/clinic"} replace />;
  }

  return <DashboardRouter />;
};
